import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, X, UserCircle, FileText, CheckCircle, Info } from "lucide-react";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";


export default function CadastroAluno({ onLogout }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [turmas, setTurmas] = useState([]);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [assinaturaUrl, setAssinaturaUrl] = useState(null);
  const [contratoFile, setContratoFile] = useState(null);
  const sigCanvas = useRef(null);
  const [formData, setFormData] = useState({
    nome: "",
    data_nascimento: "",
    cpf: "",
    endereco: "",
    telefone: "",
    turma_ids: [],
    notas_escolares: null,
    dia_vencimento_mensalidade: 10,
    valor_mensalidade: 0,
    status: "ativo",
    motivo_desligamento: "",
    data_desligamento: null,
    foto_url: null,
    contrato_assinado: false,
    contrato_data: null,
    responsavel: {
      nome: "",
      telefone: "",
      grau_parentesco: ""
    }
  });

  useEffect(() => {
    fetchTurmas();
    if (isEdit) {
      fetchAluno();
    }
  }, [id]);

  const fetchTurmas = async () => {
    try {
      const response = await api.get('/turmas');
      setTurmas(response.data);
    } catch (error) {
      toast.error("Erro ao carregar turmas");
    }
  };

  const fetchAluno = async () => {
    try {
      const response = await api.get(`/alunos/${id}`);
      const aluno = response.data;
      
      // Suporte a migração de dados antigos
      let turmaIds = aluno.turma_ids || [];
      if (!turmaIds.length && aluno.turma_id) {
        turmaIds = [aluno.turma_id];
      }
      
      setFormData({
        nome: aluno.nome,
        data_nascimento: aluno.data_nascimento,
        cpf: aluno.cpf,
        endereco: aluno.endereco,
        telefone: aluno.telefone,
        turma_ids: turmaIds,
        notas_escolares: aluno.notas_escolares || "",
        dia_vencimento_mensalidade: aluno.dia_vencimento_mensalidade || 10,
        valor_mensalidade: aluno.valor_mensalidade || 0,
        status: aluno.status || "ativo",
        motivo_desligamento: aluno.motivo_desligamento || "",
        data_desligamento: aluno.data_desligamento || null,
        foto_url: aluno.foto_url || null,
        contrato_assinado: aluno.contrato_assinado || false,
        contrato_data: aluno.contrato_data || null,
        responsavel: aluno.responsavel
      });
      
      if (aluno.foto_url) {
        setFotoPreview(aluno.foto_url);
      }
      
      if (aluno.contrato_assinado && aluno.contrato_data) {
        setAssinaturaUrl(aluno.contrato_data);
      }
    } catch (error) {
      toast.error("Erro ao carregar aluno");
    }
  };

  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Limitar tamanho a 2MB
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Foto deve ter no máximo 2MB");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setFotoPreview(base64);
        setFormData(prev => ({ ...prev, foto_url: base64 }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Se maior de idade, preencher responsável com dados do próprio aluno
      let responsavelData = formData.responsavel;
      if (isMaiorDeIdade) {
        responsavelData = {
          nome: formData.nome,
          telefone: formData.telefone,
          grau_parentesco: "proprio"
        };
      }
      
      const data = {
        ...formData,
        turma_ids: formData.turma_ids || [],
        responsavel: responsavelData
      };

      let alunoId = id;

      if (isEdit) {
        await api.put(`/alunos/${id}`, data);
        toast.success("Aluno atualizado com sucesso!");
      } else {
        const response = await api.post('/alunos', data);
        alunoId = response.data.id;
        toast.success("Aluno cadastrado com sucesso!");
      }

      // Upload de foto se houver
      if (formData.foto_url && formData.foto_url.startsWith('data:image')) {
        await api.post(`/alunos/${alunoId}/foto`, {
          foto_base64: formData.foto_url
        });
      }

      navigate('/alunos');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar aluno");
    } finally {
      setLoading(false);
    }
  };

  const toggleTurma = useCallback((turmaId) => {
    if (!turmaId) return;
    
    setFormData(prev => {
      const turmaIds = [...prev.turma_ids];
      const index = turmaIds.indexOf(turmaId);
      
      if (index > -1) {
        turmaIds.splice(index, 1);
      } else {
        turmaIds.push(turmaId);
      }
      
      return { ...prev, turma_ids: turmaIds };
    });
  }, []);

  const removeTurma = useCallback((turmaId) => {
    if (!turmaId) return;
    
    setFormData(prev => ({
      ...prev,
      turma_ids: prev.turma_ids.filter(id => id !== turmaId)
    }));
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // Quando muda para inativo, registra a data de desligamento (se ainda não houver)
      if (field === "status" && value === "inativo" && !prev.data_desligamento) {
        next.data_desligamento = new Date().toISOString().split("T")[0];
      }
      // Quando volta para ativo, mantém o histórico em motivo_desligamento mas zera a data
      // (Comentado para preservar histórico — descomente se quiser limpar)
      // if (field === "status" && value === "ativo") {
      //   next.motivo_desligamento = "";
      //   next.data_desligamento = null;
      // }
      return next;
    });
  };

  const handleResponsavelChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      responsavel: { ...prev.responsavel, [field]: value }
    }));
  };

  // Calcula se o aluno é maior de idade
  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return 0;
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mesAtual = hoje.getMonth();
    const mesNascimento = nascimento.getMonth();
    if (mesAtual < mesNascimento || (mesAtual === mesNascimento && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
    return idade;
  };

  const idade = calcularIdade(formData.data_nascimento);
  const isMaiorDeIdade = idade >= 18;

  const limparAssinatura = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    setAssinaturaUrl(null);
    setFormData(prev => ({ ...prev, contrato_assinado: false, contrato_data: null }));
  };

  const salvarAssinatura = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const assinatura = sigCanvas.current.toDataURL();
      setAssinaturaUrl(assinatura);
      setFormData(prev => ({
        ...prev,
        contrato_assinado: true,
        contrato_data: assinatura
      }));
      toast.success("Assinatura salva!");
    } else {
      toast.error("Por favor, assine antes de salvar");
    }
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            data-testid="back-button"
            variant="outline"
            size="icon"
            onClick={() => navigate('/alunos')}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              {isEdit ? "Editar Aluno" : "Novo Aluno"}
            </h1>
            <p className="text-gray-600">Preencha os dados do aluno</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} onKeyDown={(e) => {
          // Prevenir submit do formulário ao pressionar Enter em inputs
          if (e.key === 'Enter' && e.target.tagName === 'INPUT' && e.target.type !== 'submit') {
            e.preventDefault();
          }
        }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Dados do Aluno</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Foto do aluno */}
                <div className="space-y-2">
                  <Label>Foto do Aluno</Label>
                  <div className="flex items-center gap-4">
                    {fotoPreview ? (
                      <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-gray-300">
                        <img src={fotoPreview} alt="Preview" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                        <UserCircle className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleFotoChange}
                        data-testid="foto-input"
                        className="cursor-pointer"
                      />
                      <p className="text-xs text-gray-500 mt-1">Máximo 2MB (JPG, PNG)</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nome">Nome Completo *</Label>
                  <Input
                    id="nome"
                    data-testid="nome-input"
                    value={formData.nome}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_nascimento">Data de Nascimento *</Label>
                  <Input
                    id="data_nascimento"
                    data-testid="data-nascimento-input"
                    type="date"
                    value={formData.data_nascimento}
                    onChange={(e) => handleChange('data_nascimento', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cpf">CPF *</Label>
                  <Input
                    id="cpf"
                    data-testid="cpf-input"
                    value={formData.cpf}
                    onChange={(e) => handleChange('cpf', e.target.value)}
                    placeholder="000.000.000-00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone *</Label>
                  <Input
                    id="telefone"
                    data-testid="telefone-input"
                    value={formData.telefone}
                    onChange={(e) => handleChange('telefone', e.target.value)}
                    placeholder="(00) 00000-0000"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endereco">Endereço *</Label>
                  <Input
                    id="endereco"
                    data-testid="endereco-input"
                    value={formData.endereco}
                    onChange={(e) => handleChange('endereco', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Turmas / Modalidades</Label>
                  <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
                    {formData.turma_ids.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {formData.turma_ids
                          .map(turmaId => turmas.find(t => t.id === turmaId))
                          .filter(turma => turma !== undefined)
                          .map((turma) => (
                            <Badge key={turma.id} variant="secondary" className="pl-3 pr-2 py-1.5" data-testid={`selected-turma-${turma.id}`}>
                              {turma.nome} - {turma.modalidade}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  removeTurma(turma.id);
                                }}
                                className="ml-2 hover:bg-gray-300 rounded-full p-0.5"
                                data-testid={`remove-turma-${turma.id}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                      </div>
                    )}
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {turmas.map((turma) => (
                        <div key={turma.id} className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded" data-testid={`turma-option-${turma.id}`}>
                          <Checkbox
                            id={`turma-${turma.id}`}
                            checked={formData.turma_ids.includes(turma.id)}
                            onCheckedChange={() => toggleTurma(turma.id)}
                            data-testid={`checkbox-turma-${turma.id}`}
                          />
                          <label
                            htmlFor={`turma-${turma.id}`}
                            className="flex-1 text-sm cursor-pointer"
                          >
                            <div className="font-medium">{turma.nome}</div>
                            <div className="text-xs text-gray-500">{turma.modalidade} - {turma.faixa_etaria}</div>
                          </label>
                        </div>
                      ))}
                      {turmas.length === 0 && (
                        <div className="text-sm text-gray-500 text-center py-4">
                          Nenhuma turma cadastrada
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Selecione uma ou mais turmas/modalidades para o aluno
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notas_escolares">Notas Escolares</Label>
                  <Input
                    id="notas_escolares"
                    data-testid="notas-escolares-input"
                    value={formData.notas_escolares || ''}
                    onChange={(e) => handleChange('notas_escolares', e.target.value)}
                    placeholder="Ex: Matemática: 8.5, Português: 9.0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dia_vencimento">Dia do Vencimento da Mensalidade *</Label>
                  <Select 
                    value={formData.dia_vencimento_mensalidade.toString()} 
                    onValueChange={(value) => handleChange('dia_vencimento_mensalidade', parseInt(value))}
                  >
                    <SelectTrigger data-testid="vencimento-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 31}, (_, i) => i + 1).map(dia => (
                        <SelectItem key={dia} value={dia.toString()}>
                          Dia {dia}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="valor_mensalidade">Valor da Mensalidade (R$) *</Label>
                  <Input
                    id="valor_mensalidade"
                    data-testid="valor-mensalidade-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.valor_mensalidade}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      // Arredondar para 2 casas decimais para evitar problemas de precisão
                      handleChange('valor_mensalidade', Math.round(value * 100) / 100);
                    }}
                    placeholder="Ex: 150.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status *</Label>
                  <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">Ativo</SelectItem>
                      <SelectItem value="inativo">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Motivo do Desligamento - aparece quando status = inativo */}
            {formData.status === "inativo" && (
              <Card className="border-orange-200 bg-orange-50/40" data-testid="card-motivo-desligamento">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Info className="w-4 h-4 text-orange-600" />
                    Motivo do desligamento
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="data_desligamento">Data do desligamento</Label>
                    <Input
                      id="data_desligamento"
                      type="date"
                      value={formData.data_desligamento || ""}
                      onChange={(e) => handleChange('data_desligamento', e.target.value)}
                      data-testid="input-data-desligamento"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="motivo_desligamento">
                      Explique o motivo do desligamento
                    </Label>
                    <textarea
                      id="motivo_desligamento"
                      data-testid="textarea-motivo-desligamento"
                      value={formData.motivo_desligamento || ""}
                      onChange={(e) => handleChange('motivo_desligamento', e.target.value)}
                      placeholder="Ex: Mudança de cidade, falta de interesse, problemas financeiros, etc."
                      rows={4}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    <p className="text-xs text-gray-500">
                      Esta informação fica registrada no histórico do aluno.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Aviso de Maior de Idade */}
            {formData.data_nascimento && (
              <Alert className={isMaiorDeIdade ? "bg-blue-50 border-blue-200" : "bg-gray-50"}>
                <Info className={`w-4 h-4 ${isMaiorDeIdade ? "text-blue-600" : "text-gray-600"}`} />
                <AlertDescription className="text-sm">
                  {isMaiorDeIdade ? (
                    <span className="text-blue-700">
                      <strong>Aluno maior de idade ({idade} anos)</strong> - Os dados do responsável são opcionais.
                    </span>
                  ) : (
                    <span className="text-gray-600">
                      Idade do aluno: <strong>{idade} anos</strong> - Dados do responsável obrigatórios.
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Dados do Responsável - Obrigatório apenas para menores */}
            {!isMaiorDeIdade && (
            <Card>
              <CardHeader>
                <CardTitle>Dados do Responsável</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="responsavel_nome">Nome do Responsável *</Label>
                  <Input
                    id="responsavel_nome"
                    data-testid="responsavel-nome-input"
                    value={formData.responsavel.nome}
                    onChange={(e) => handleResponsavelChange('nome', e.target.value)}
                    required={!isMaiorDeIdade}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="responsavel_telefone">Telefone do Responsável *</Label>
                  <Input
                    id="responsavel_telefone"
                    data-testid="responsavel-telefone-input"
                    value={formData.responsavel.telefone}
                    onChange={(e) => handleResponsavelChange('telefone', e.target.value)}
                    placeholder="(00) 00000-0000"
                    required={!isMaiorDeIdade}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="grau_parentesco">Grau de Parentesco *</Label>
                  <Select
                    value={formData.responsavel.grau_parentesco}
                    onValueChange={(value) => handleResponsavelChange('grau_parentesco', value)}
                  >
                    <SelectTrigger data-testid="parentesco-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pai">Pai</SelectItem>
                      <SelectItem value="mae">Mãe</SelectItem>
                      <SelectItem value="avo">Avô/Avó</SelectItem>
                      <SelectItem value="tio">Tio/Tia</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Contrato e Assinatura Digital
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Upload de Contrato Exemplo */}
                <div className="space-y-2">
                  <Label>Anexar Modelo de Contrato (Opcional)</Label>
                  {contratoFile ? (
                    <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="w-8 h-8 text-green-600" />
                          <div>
                            <p className="text-sm font-semibold text-green-800">{contratoFile.name}</p>
                            <p className="text-xs text-green-600">
                              {(contratoFile.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setContratoFile(null);
                            toast.info("Contrato removido");
                          }}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed rounded-lg p-4 bg-gray-50">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        id="contrato-upload"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setContratoFile(file);
                            toast.success(`Contrato ${file.name} anexado!`);
                          }
                        }}
                      />
                      <label
                        htmlFor="contrato-upload"
                        className="flex flex-col items-center cursor-pointer"
                      >
                        <FileText className="w-8 h-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">Clique para anexar PDF ou Word</span>
                        <span className="text-xs text-gray-500 mt-1">Seu modelo personalizado de contrato</span>
                      </label>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Termo de Matrícula</Label>
                  <div className="border rounded-lg p-4 bg-gray-50 text-sm max-h-48 overflow-y-auto">
                    <p className="font-semibold mb-2">TERMO DE MATRÍCULA E RESPONSABILIDADE</p>
                    <p className="mb-2">
                      Eu, responsável pelo(a) aluno(a) acima identificado(a), declaro estar ciente e de acordo com as normas
                      e regulamentos da escola esportiva, comprometendo-me a:
                    </p>
                    <ul className="list-disc list-inside space-y-1 mb-2">
                      <li>Efetuar o pagamento das mensalidades na data de vencimento</li>
                      <li>Comunicar ausências com antecedência</li>
                      <li>Manter os dados cadastrais atualizados</li>
                      <li>Respeitar professores, funcionários e demais alunos</li>
                      <li>Autorizar a participação do aluno em atividades esportivas</li>
                    </ul>
                    <p className="text-xs text-gray-600">
                      Declaro também estar ciente de que a não observância destas normas poderá acarretar no cancelamento da matrícula.
                    </p>
                  </div>
                </div>

                {/* Assinatura Digital com Canvas */}
                <div className="space-y-2">
                  <Label>Assinatura Digital *</Label>
                  {assinaturaUrl ? (
                    <div className="border rounded-lg p-4 bg-white">
                      <img src={assinaturaUrl} alt="Assinatura" className="w-full max-h-40 object-contain border rounded" />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={limparAssinatura}
                        size="sm"
                        className="mt-2 w-full"
                      >
                        Limpar Assinatura
                      </Button>
                    </div>
                  ) : (
                    <div className="border-2 border-gray-300 rounded-lg bg-white">
                      <SignatureCanvas
                        ref={sigCanvas}
                        canvasProps={{
                          className: "w-full h-40 cursor-crosshair",
                          style: { touchAction: 'none' }
                        }}
                        backgroundColor="#ffffff"
                      />
                      <div className="flex gap-2 p-2 border-t">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={limparAssinatura}
                          size="sm"
                          className="flex-1"
                        >
                          Limpar
                        </Button>
                        <Button
                          type="button"
                          onClick={salvarAssinatura}
                          size="sm"
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                        >
                          Salvar Assinatura
                        </Button>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">Desenhe sua assinatura no espaço acima</p>
                </div>

                {/* Aceite do Contrato com Checkbox */}
                <div className="space-y-2">
                  <Label>Aceite do Contrato *</Label>
                  {formData.contrato_assinado ? (
                    <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-green-500 rounded-full p-2">
                          <CheckCircle className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-green-800">Contrato Aceito</p>
                          <p className="text-sm text-green-700">
                            Aceito em: {formData.contrato_data ? new Date(formData.contrato_data).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              contrato_assinado: false,
                              contrato_data: null
                            }));
                            toast.info("Aceite cancelado");
                          }}
                          size="sm"
                          className="text-red-600"
                        >
                          Cancelar Aceite
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-gray-300 rounded-lg p-6 bg-white space-y-4">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="aceite-contrato"
                          className="mt-1 h-5 w-5 rounded border-gray-300"
                          checked={false}
                          readOnly
                        />
                        <label htmlFor="aceite-contrato" className="text-sm text-gray-700 flex-1">
                          Declaro que li e concordo com os termos de matrícula acima apresentados, 
                          comprometendo-me a cumprir todas as normas e regulamentos da escola esportiva.
                        </label>
                      </div>
                      <Button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            contrato_assinado: true,
                            contrato_data: new Date().toISOString()
                          }));
                          toast.success("Contrato aceito com sucesso!");
                        }}
                        className="w-full bg-green-600 hover:bg-green-700"
                        data-testid="aceitar-contrato"
                      >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Aceito os Termos do Contrato
                      </Button>
                      <p className="text-xs text-gray-500 text-center">
                        Ao clicar no botão acima, você confirma o aceite eletrônico do contrato
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 flex justify-end gap-4">
            <Button
              data-testid="cancel-button"
              type="button"
              variant="outline"
              onClick={() => navigate('/alunos')}
            >
              Cancelar
            </Button>
            <Button
              data-testid="submit-button"
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600"
            >
              {loading ? "Salvando..." : (isEdit ? "Atualizar" : "Cadastrar")}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
